import React, { useEffect, useState } from "react";
import { getAuth } from "firebase/auth";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { db } from "../firebase";
import Spinner from "../components/Spinner";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { FaUserCircle } from 'react-icons/fa'; // For default avatar

const emptyParentProfile = {
  fullName: "",
  email: "",
  mobile: "",
  city: "",
  localArea: "",
  profession: "",
  childNeeds: []
};

export default function ParentProfile() {
  const [user, setUser] = useState(null);
  const [parentProfile, setParentProfile] = useState(emptyParentProfile);
  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [error, setError] = useState("");
  const [incomplete, setIncomplete] = useState(false);
  const firstName = parentProfile.fullName?.split(' ')[0] || 'Parent';

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = auth.onAuthStateChanged(async (firebaseUser) => {
      if (firebaseUser) {
        setUser(firebaseUser);
        await fetchProfile(firebaseUser);
      } else {
        setUser(null);
        setLoading(false);
      }
    });
    return () => unsubscribe();
  }, []);

  const fetchProfile = async (user) => {
    setLoading(true);
    setError("");
    try {
      const parentSnap = await getDoc(doc(db, "users", user.uid));
      let parentData = parentSnap.exists() ? parentSnap.data() : {};
      parentData.email = user.email;
      parentData.fullName = user.displayName || parentData.fullName || "";
      setParentProfile({
        fullName: parentData.fullName || "",
        email: parentData.email || "",
        mobile: parentData.mobile || "",
        city: parentData.city || "",
        localArea: parentData.localArea || "",
        profession: parentData.profession || "",
        childNeeds: parentData.childNeeds || []
      });
      setIncomplete(!parentData.fullName || !parentData.mobile || !parentData.city);
      setLoading(false);
    } catch (e) {
      setError("Failed to fetch profile. " + e.message);
      setLoading(false);
    }
  };

  // Handlers
  const handleParentChange = (e) => {
    const { name, value } = e.target;
    setParentProfile((p) => ({ ...p, [name]: value }));
  };
  const handleChildNeedsChange = (e) => {
    const { value, checked } = e.target;
    setParentProfile((p) => {
      let updated = [...(p.childNeeds || [])];
      if (checked) {
        if (!updated.includes(value)) updated.push(value);
      } else {
        updated = updated.filter((v) => v !== value);
      }
      return { ...p, childNeeds: updated };
    });
  };
  const handleParentSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      await setDoc(doc(db, "users", user.uid), parentProfile, { merge: true });
      setEditMode(false);
      setShowToast(true);
      setTimeout(() => setShowToast(false), 2500);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 2000);
      fetchProfile(user);
    } catch (e) {
      setError("Failed to save profile. " + e.message);
    }
    setSaving(false);
  };
  const handleParentCancel = () => {
    fetchProfile(user);
    setEditMode(false);
  };

  if (loading) return <Spinner />;

  return (
    <>
      <Header />
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-3xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
          {/* New Welcome Banner */}
          <div className="bg-gradient-to-r from-blue-50 to-purple-100 text-lg font-medium rounded-xl px-6 py-4 mb-6 shadow-sm tracking-tight">
            👋 Welcome, {firstName}! You are not alone in this journey. Your profile helps us support your child's strengths better.
          </div>

          {showToast && (
            <div className="fixed top-20 right-5 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 animate-fade-in-out">
              Profile saved successfully!
            </div>
          )}
          {error && (
            <div className="fixed top-20 right-5 bg-red-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 animate-fade-in-out">
              {error}
            </div>
          )}

          {/* Profile View or Edit Form Container */}
          <div className={`bg-white rounded-xl shadow-md ${editMode ? 'p-4 md:p-6' : 'p-0'}`}> 
            {editMode ? (
              <form onSubmit={handleParentSave} className="space-y-6 p-4 md:p-6">
                <h2 className="text-xl font-semibold text-gray-800 mb-4">Edit Your Profile</h2>
                {/* Existing form fields - styles might need minor review for consistency if desired later */}
                <div>
                  <label htmlFor="fullName" className="block text-sm font-medium text-gray-700">Full Name</label>
                  <input type="text" name="fullName" id="fullName" value={parentProfile.fullName || ''} onChange={handleParentChange} required className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700">Email (Cannot be changed)</label>
                  <input type="email" name="email" id="email" value={parentProfile.email || ''} readOnly className="mt-1 block w-full rounded-md border-gray-300 bg-gray-100 shadow-sm sm:text-sm" />
                </div>
                <div>
                  <label htmlFor="mobile" className="block text-sm font-medium text-gray-700">Mobile Number</label>
                  <input type="tel" name="mobile" id="mobile" value={parentProfile.mobile || ''} onChange={handleParentChange} required className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" />
                </div>
                <div>
                  <label htmlFor="city" className="block text-sm font-medium text-gray-700">City</label>
                  <input type="text" name="city" id="city" value={parentProfile.city || ''} onChange={handleParentChange} required className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" />
                </div>
                <div>
                  <label htmlFor="localArea" className="block text-sm font-medium text-gray-700">Local Area / Landmark</label>
                  <input type="text" name="localArea" id="localArea" value={parentProfile.localArea || ''} onChange={handleParentChange} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" />
                </div>
                <div>
                  <label htmlFor="profession" className="block text-sm font-medium text-gray-700">Your Profession</label>
                  <input type="text" name="profession" id="profession" value={parentProfile.profession || ''} onChange={handleParentChange} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Child Needs</label>
                  <div className="mt-2 space-y-2">
                    {['Speech Therapy', 'Occupational Therapy', 'Behaviour Therapy', 'Remedial Education', 'Special Education', 'Counselling', 'Other'].map((need) => (
                      <label key={need} className="inline-flex items-center mr-4">
                        <input type="checkbox" className="rounded border-gray-300 text-indigo-600 shadow-sm focus:border-indigo-300 focus:ring focus:ring-offset-0 focus:ring-indigo-200 focus:ring-opacity-50" value={need} checked={parentProfile.childNeeds?.includes(need) || false} onChange={handleChildNeedsChange} />
                        <span className="ml-2 text-sm text-gray-700">{need}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div className="flex justify-end items-center mt-6 pt-4 border-t border-gray-200">
                  <button type="button" onClick={handleParentCancel} className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium py-2 px-4 rounded-md focus:outline-none focus:shadow-outline mr-3">Cancel</button>
                  <button type="submit" disabled={saving} className="bg-indigo-600 hover:bg-indigo-700 text-white font-medium py-2 px-4 rounded-md focus:outline-none focus:shadow-outline disabled:opacity-60">
                    {saving ? 'Saving...' : 'Save Profile'}
                  </button>
                </div>
              </form>
            ) : (
              // New Profile Card View
              <div className="max-w-2xl mx-auto">
                <div className="bg-white p-6 rounded-xl">
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center">
                    <div className="flex gap-4 items-center mb-4 sm:mb-0">
                      {user?.photoURL ? (
                        <img src={user.photoURL} alt="Profile" className="w-16 h-16 rounded-full object-cover" />
                      ) : (
                        <FaUserCircle className="w-16 h-16 rounded-full text-gray-300" />
                      )}
                      <div>
                        <div className="flex items-center">
                            <h2 className="text-xl font-semibold text-gray-900 tracking-tight">{parentProfile.fullName || 'Parent User'}</h2>
                            {incomplete && (
                                <span className="ml-2 bg-red-100 text-red-700 text-xs font-medium px-2 py-0.5 rounded-full">Incomplete Profile</span>
                            )}
                        </div>
                        <p className="text-sm text-gray-600 font-medium">{parentProfile.email}</p>
                        <span className="bg-blue-100 text-blue-700 text-xs font-medium px-2 py-0.5 rounded-full mt-1 inline-block">Parent</span>
                      </div>
                    </div>
                    <button 
                      onClick={() => setEditMode(true)} 
                      className="bg-purple-100 text-purple-800 font-medium px-4 py-2 rounded-md hover:bg-purple-200 text-sm whitespace-nowrap self-start sm:self-center">
                      ✏️ Edit Profile
                    </button>
                  </div>

                  <div className="mt-6 pt-4 border-t border-gray-200 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4 text-sm text-gray-700 font-medium tracking-tight">
                    <div>
                      <strong className="text-gray-900">Mobile:</strong> {parentProfile.mobile || <span className="text-red-500">Not Provided</span>}
                    </div>
                    <div>
                      <strong className="text-gray-900">City:</strong> {parentProfile.city || <span className="text-red-500">Not Provided</span>}
                    </div>
                    <div>
                      <strong className="text-gray-900">Local Area:</strong> {parentProfile.localArea || <span className="text-gray-400">Not Provided</span>}
                    </div>
                    <div>
                      <strong className="text-gray-900">Profession:</strong> {parentProfile.profession || <span className="text-gray-400">Not Provided</span>}
                    </div>
                    <div className="sm:col-span-2">
                      <strong className="text-gray-900">Child Needs:</strong>
                      {parentProfile.childNeeds && parentProfile.childNeeds.length > 0 ? (
                        parentProfile.childNeeds.map((tag, index) => (
                          <span key={index} className="inline-block bg-blue-100 text-blue-700 text-xs font-medium px-2 py-1 rounded-full mr-2 mt-1">{tag}</span>
                        ))
                      ) : (
                        <span className="text-gray-400 ml-1">No specific needs listed. You can add these by editing your profile.</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Incomplete Profile CTA */}
          {!editMode && incomplete && (
            <div className="mt-6 text-sm text-red-600 bg-red-50 border border-red-200 p-3 rounded-lg text-center max-w-2xl mx-auto shadow-sm">
              <span className="font-bold">⚠ Important:</span> Some profile details are missing (e.g., Mobile or City). Completing your profile helps us find the right experts for your child faster.
            </div>
          )}

        </div>
      </div>
      <Footer />
    </>
  );
}
